'use client';

import { useCallback, useState } from 'react';

import type { PostSearchItem } from '@ps/api/types';
import type {
  CommunityChannelMessageLink,
  CommunityChannelMessagePostLink,
} from '@ps/models/lib/community-channel';

import { postSearchItemToMessageLink } from '../../../functions/links/postSearchItemToMessageLink';

/**
 * Composer-side staging for `CommunityChannelMessageLink` items the user has picked
 * (today only post links). Unlike attachments these don't have an
 * upload phase — the snapshot is computed at pick time and stored
 * verbatim until send.
 *
 * De-duplicates by link `id` so picking the same post twice from the
 * modal doesn't stack two copies.
 */
export interface UseComposerLinksResult {
  links: CommunityChannelMessagePostLink[];
  /** All staged links, typed for `useSendChannelMessage`'s payload. */
  payload: CommunityChannelMessageLink[];
  addPost: (post: PostSearchItem) => void;
  remove: (id: string) => void;
  clear: () => void;
  hasLinks: boolean;
}

export function useComposerLinks(): UseComposerLinksResult {
  const [links, setLinks] = useState<CommunityChannelMessagePostLink[]>([]);

  const addPost = useCallback((post: PostSearchItem) => {
    setLinks(prev => {
      if (prev.some(link => link.id === post.id)) return prev;
      return [...prev, postSearchItemToMessageLink(post)];
    });
  }, []);

  const remove = useCallback((id: string) => {
    setLinks(prev => prev.filter(link => link.id !== id));
  }, []);

  const clear = useCallback(() => {
    setLinks([]);
  }, []);

  return {
    links,
    payload: links,
    addPost,
    remove,
    clear,
    hasLinks: links.length > 0,
  };
}
